import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGUAGES } from "@/i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Discreet language switcher (flag + name). Persists via i18next localStorage detector.
// Two visual variants: "dark" (on dark brand surfaces) and "light" (default).
export function LanguageSwitcher({ variant = "light", className = "" }) {
  const { i18n, t } = useTranslation();
  const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0];

  const triggerClass =
    variant === "dark"
      ? "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
      : "border-border bg-background/60 text-muted-foreground hover:text-foreground";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("common.language")}
          data-testid="language-switcher"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium backdrop-blur transition-colors ${triggerClass} ${className}`}
        >
          <Globe className="h-3.5 w-3.5" />
          <span className="text-sm leading-none">{current.flag}</span>
          <span className="hidden sm:inline">{current.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => i18n.changeLanguage(l.code)}
            className="flex items-center gap-2 text-sm"
          >
            <span className="text-base leading-none">{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {l.code === current.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
